import {type FormatXMLElementFn, type PrimitiveType} from 'intl-messageformat';
import {memo} from 'react';
import {useIntl} from 'react-intl';

import {ShortcutKeyVariant, ShortcutKey} from 'components/shortcut_key';
import {isMac} from 'utils/utils';

import {isMessageDescriptor, type KeyboardShortcutDescriptor} from './keyboard_shortcuts';

import './keyboard_shortcuts_sequence.scss';

type Props = {
    shortcut: KeyboardShortcutDescriptor;
    values?: Record<string, PrimitiveType | FormatXMLElementFn<string, string>>;
    hideDescription?: boolean;
    hoistDescription?: boolean;
    isInsideTooltip?: boolean;
};

function normalizeShortcutDescriptor(shortcut: KeyboardShortcutDescriptor) {
    if (isMessageDescriptor(shortcut)) {
        return shortcut;
    }
    const {default: standard, mac} = shortcut;
    return isMac() && mac ? mac : standard;
}

const KEY_SEPARATOR = '|';

export function useKeyboardShortcut(
    shortcut: KeyboardShortcutDescriptor,
    values?: Record<string, PrimitiveType | FormatXMLElementFn<string, string>>,
) {
    const {formatMessage} = useIntl();
    const shortcutText = formatMessage(normalizeShortcutDescriptor(shortcut), values);
    const splitShortcut = shortcutText.split('\t');

    let description;
    let keys;

    if (splitShortcut.length > 1) {
        description = splitShortcut[0]!;
        keys = splitShortcut[1]!;
    } else if (splitShortcut[0]!.includes(KEY_SEPARATOR)) {
        keys = splitShortcut[0]!;
    } else {
        description = splitShortcut[0]!;
    }

    return {keys: keys?.split(KEY_SEPARATOR) || [], description};
}

function KeyboardShortcutSequence({shortcut, values, hideDescription, hoistDescription, isInsideTooltip}: Props) {
    const {keys, description} = useKeyboardShortcut(shortcut, values);

    return (
        <>
            {hoistDescription && !hideDescription && description?.replace(/:{1,2}$/, '')}
            <div className='shortcut-line'>
                {!hoistDescription && !hideDescription && description && <span>{description}</span>}
                {keys.map((key) => (
                    <ShortcutKey
                        key={key}
                        variant={isInsideTooltip ? ShortcutKeyVariant.Tooltip : ShortcutKeyVariant.ShortcutModal}
                    >
                        {key}
                    </ShortcutKey>
                ))}
            </div>
        </>
    );
}

export default memo(KeyboardShortcutSequence);
